<template>
  <main>
    <FadeIn class="isolate overflow-hidden bg-faun">
      
      <div class="mx-auto max-w-7xl px-6 pb-96 pt-24 text-center sm:pt-24 lg:px-8">
        <div class="mx-auto max-w-4xl">
          <div class="mb-2 md:mb-4">
            <span class="rounded-full mr-2 sm:mr-0 mb-2 sm:mb-0 bg-[#5a6ded] px-2 py-0 text-xs inline-block font-semibold leading-6 text-white no-underline">
              Pricing
            </span>
          </div>
          <h1 id="main-title" class="text-3xl leading-[1.25] sm:text-4xl sm:leading-[1.2] md:text-5xl md:leading-[1.12] font-bold tracking-tight text-gray-900 lora lg:max-w-3xl lg:mx-auto">Tailored solutions for every organisation</h1>
          <p class="text-subtitle max-w-3xl mx-auto">
            Elker offers bespoke reporting solutions, designed to meet the unique needs of small-to-medium-sized organisations and the complex demands of large enterprises.<span class="hidden lg:inline-block"> If you are unsure about what you might need, <NuxtLink to="/contact">contact us</NuxtLink> for a free consultation.</span>
          </p>
        </div>
      </div>

      <div class="flow-root bg-[#f9f8f6] pb-24 pb-8 md:pb-32">
        <div class="-mt-80">
        
          <div class="mx-auto md:max-w-[640px] lg:max-w-[800px] xl:max-w-[960px] px-6 md:px-8 grid grid-cols-1 lg:gap-6 xl:gap-8 lg:grid-cols-2">  
            <div 
            v-for="tier in tiers"
            class="border-2 rounded-xl md:rounded-3xl p-4 md:p-8 xl:p-8 shadow-lg mb-8"
            :class="tier.background + ' ' + tier.borderColor"
            >
              <!-- <h3 class="text-lg/8 font-semibold" :class="tier.headingColor">
                {{ tier.name }}
              </h3> -->
              <h3 class="text-4xl font-semibold tracking-tight" :class="tier.headingColor">
                {{ tier.name }}
              </h3>
              <div class="mt-4 text-[18px]" :class="tier.textColor">
                {{ tier.description }}
              </div>
              <!-- <div class="mt-6 text-sm/6 font-semibold" :class="tier.textColor">
                <span v-if="tier.price !== 'Custom'">From</span>
                <span v-else>&nbsp;</span>
              </div>
              <div class=" flex items-baseline gap-x-1">
                <span class="text-4xl font-semibold tracking-tight" :class="tier.headingColor">
                  {{ tier.price }}
                </span>
                <span 
                class="text-sm/6 font-semibold" 
                :class="tier.textColor"
                v-if="tier.price !== 'Custom'"
                >annually</span>
              </div> -->
              <button
              :href="tier.href" 
              class="mt-6 w-full block rounded-md px-3 py-2 text-center text-sm/6 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              :class="tier.buttonBackground + ' ' + tier.buttonTextColor"
              @click="tier.function"
              >{{ tier.cta }}</button>
              <ul role="list" class="mt-8 space-y-3 text-sm/6 xl:mt-10" :class="tier.textColor">
                <li v-for="feature in tier.features" :key="feature" class="flex gap-x-3">
                  <Icon 
                  name="iconoir:check" 
                  class="h-6 w-5 flex-none"
                  :class="tier.iconColor"
                  />
                  {{ feature }}
                </li>
              </ul>
            </div>

          </div>
          <div class="hidden md:block">
            <div class="mt-12 text-center">
              <p class="text-sm/6 text-gray-500">A full list of features for each tier is below.</p>
            </div>
          </div>
        </div>
      </div>
    </FadeIn>

    <FadeIn class="min-h-[800px] bg-[#f9f8f6] md:block">
      <!-- Sticky pricing headers -->
      <div class="sticky top-[70px] lg:top-[90px] z-50 bg-[#f9f8f6] border-b-2 border-[#e8e5de]">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="grid grid-cols-4 md:grid-cols-3 gap-0">
            <div class="col-span-2 md:col-span-1 p-4">
              <h2 class="text-2xl font-semibold hidden md:block text-gray-900">Features</h2>
              <button 
              class="bg-[#5a6ded] border-2 border-[#3645d0] text-white inline-flex md:hidden items-center transition justify-center whitespace-nowrap rounded-md px-2.5 py-1 text-sm md:text-base font-semibold"
              @click="requestQuote"
              >
                Request quote
              </button>
            </div>

            <div 
            v-for="(tier, index) in tiers"
            class="col-span-1 p-2 md:p-4"
            :class="tier.background, { 'rounded-tl-lg': index === 0, 'rounded-tr-lg': index === tiers.length - 1 }"
            >
              <div class="flex flex-col items-center md:items-baseline justify-center h-full">
                <h3 class="text-sm md:text-2xl font-semibold"
                :class="tier.headingColor"
                >{{ tier.name }}</h3>
                <p class="mt-1 text-sm hidden md:block" :class="tier.textColor">{{ tier.description }}</p>
                <button 
                class="hidden md:inline-flex items-center transition justify-center whitespace-nowrap rounded-md px-2.5 py-1 mt-4 text-base font-semibold"
                :class="tier.buttonBackground + ' ' + tier.buttonTextColor"
                @click="tier.function"
                >
                  {{ tier.cta }}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Feature sections -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="space-y-6">
          <div v-for="(section, index) in sections" :key="index" class="border-2 border-[#c7cedf] rounded-lg overflow-hidden">
            <button 
            class="w-full px-2 md:px-6 py-2 md:py-4 bg-faun transition-colors flex items-center justify-between"
            :class="section.isOpen ? 'bg-[#e0e3f5]': 'bg-white'"
            @click="toggleSection(index)"
            :aria-expanded="section.isOpen"
            :aria-controls="`section-content-${index}`"
            >
              <h3 class="text-lg md:text-xl font-semibold text-gray-900">{{ section.title }}</h3>
              <Icon 
              name="iconoir:nav-arrow-down" 
              class="w-[25px] h-[25px] text-gray-900 transition-transform duration-200" 
              :class="{ 'rotate-180': section.isOpen }"
              aria-hidden="true"
              />
            </button>

            <!-- Section content -->
            <div 
              v-show="section.isOpen" 
              class="border-t border-[#c7cedf] bg-white"
              :id="`section-content-${index}`"
              role="region"
              :aria-labelledby="`section-${index}`"
            >
              <div v-for="(feature, featureIndex) in section.features" :key="featureIndex" class="grid grid-cols-4 md:grid-cols-3 gap-3 md:gap-8 px-2 md:px-4 md:px-6 py-2 md:py-4 border-b-2 border-[#c7cedf] last:border-b-0">
                <div class="col-span-2 md:col-span-1 pt-[2px]">
                  <button
                  v-if="feature.description"
                  class="leading-6 text-base cursor-help text-left w-full"
                  @click="tooltip(feature.title, feature.description)"
                  :aria-label="`Get more information about ${feature.title}`"
                  >
                    <div class="font-[600] text-gray-900 leading-[1.2]">{{ feature.title }}</div>
                    <div class="text-[12px] md:text-[13px] text-gray-500 leading-[1.2] md:leading-4 mt-2">
                      {{ feature.subtitle }} 
                      <Icon name="iconoir:info-circle" class="hidden md:inline w-4 h-4 relative top-[4px] text-gray-400" aria-hidden="true" />
                    </div>
                  </button>
                  <div v-else-if="feature.subtitle" class="leading-6 text-base">
                    <div class="font-[600] text-gray-900 leading-[1.2]">{{ feature.title }}</div>
                    <div class="text-[12px] md:text-[13px] text-gray-500 leading-[1.2] md:leading-4 mt-2">
                      {{ feature.subtitle }} 
                    </div>
                  </div>
                  <div v-else class="leading-6 text-base">
                    <div class="font-[600] text-gray-900 leading-[1.2]">{{ feature.title }}</div>
                  </div>
                </div>
                <div class="col-span-1 md:text-center break-all md:break-normal hyphens-auto md:hyphens-none text-sm md:text-base">
                  <template v-if="typeof feature.core === 'boolean'">
                    <Icon 
                      v-if="feature.core"
                      name="iconoir:check-circle-solid" 
                      class="w-5 h-5 text-blue mx-auto"
                    />
                    <Icon 
                      v-else
                      name="iconoir:xmark" 
                      class="w-5 h-5 text-gray mx-auto"
                    />
                  </template>
                  <span v-else>{{ feature.core }}</span>
                </div>
                <div class="col-span-1 md:text-center break-all md:break-normal hyphens-auto md:hyphens-none text-sm md:text-base">
                  <template v-if="typeof feature.enterprise === 'boolean'">
                    <Icon 
                      v-if="feature.enterprise"
                      name="iconoir:check-circle-solid" 
                      class="w-5 h-5 text-blue mx-auto"
                    />
                    <Icon 
                      v-else
                      name="iconoir:xmark" 
                      class="w-5 h-5 text-gray mx-auto"
                    />
                  </template>
                  <span v-else>{{ feature.enterprise }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FadeIn>

    <template v-if="page?.dynamic">
      <template v-for="(section, index) in page?.dynamic">
        <component 
        :is="getSectionComponent(section.collection)" 
        :sectionIndex="index" 
        :content="section.item"
        :sectionTotal="page.dynamic.length"
        />
      </template>
    </template>

    <ElementBookDemoCta />

    <TransitionRoot as="template" :show="quoteOpen">
      <Dialog as="div" class="relative z-10" @close="quoteOpen = false">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full justify-center p-4 text-center items-center">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative transform overflow-hidden rounded-xl bg-[#f9f8f6] text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">

                <button 
                  @click="quoteOpen = false" 
                  class="absolute top-4 right-4 text-gray-900 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1"
                  aria-label="Close dialog"
                >
                  <Icon 
                  name="iconoir:xmark" 
                  class="w-6 h-6"
                  aria-hidden="true"
                  />
                </button>
                
                <section class="quiz p-4 md:p-6 lg:p-8" v-if="!sent">
                  <div class="my-4 max-w-[264px] sm:max-w-[400px] lg:max-w-[436px] ml-auto mr-auto align-left">
                    <h2 class="text-2xl md:text-3xl lora font-bold text-gray-900 mb-6">
                      {{ selectedTier !== 'Core' ? 'Contact us' : 'Request a proposal' }}
                    </h2>

                    <!-- Name -->
                    <div class="mb-6">
                      <label for="name" :class="classes.heading">Full name</label>
                      <input
                      id="name"
                      placeholder="Your Name"
                      v-model="name" @focus="formTracking.start()"
                      :class="classes.input"
                      type="text"
                      autocomplete="name"
                      >
                    </div>

                    <!-- Email -->
                    <div class="mb-6">
                      <label for="email" :class="classes.heading">Work email address</label>
                      <div :class="classes.subtitle">
                        Please use a work email address.
                      </div>
                      <input
                      @blur="blurEmailField()"
                      id="email"
                      placeholder="yourname@example.com"
                      type="email"
                      v-model="email" @focus="formTracking.start()"
                      :class="classes.input"
                      autocomplete="email"
                      >
                      <div v-if="emailError" class="text-red-500 text-sm font-normal mt-2">
                        {{ emailError }}
                      </div>
                    </div>

                    <div class="mb-6" v-show="genericEmail">
                      <label for="organisation" :class="classes.heading">Organisation</label>
                      <div :class="classes.subtitle">
                        Enter the name of your organisation or consultancy firm.
                      </div>
                      <input
                      id="organisation"
                      placeholder="Organisation name"
                      v-model="organisation" @focus="formTracking.start()"
                      type="text"
                      :class="classes.input"
                      >
                    </div>

                    <!-- Size -->
                    <div class="mb-6">
                      <label for="size" :class="classes.heading">Size of organisation</label>
                      <div :class="classes.subtitle">
                        The approximate number of employees, contractors or members in your organisation that will have the ability to report.
                      </div>
                      <input
                      id="size"
                      type="number"
                      v-model="size" @focus="formTracking.start()"
                      pattern="[0-9]*"
                      placeholder="Number of people"
                      :class="[ classes.input ]"
                      >
                    </div>

                    <!-- Tiers -->
                    <fieldset class="mb-6 quiz__outcomes">
                      <legend :class="classes.heading">Tier</legend>
                      <div :class="classes.subtitle">
                        The selected tier your organisation is enquiring about.
                      </div>
                      <div class="mt-4 w-full grid grid-cols-3 gap-2 justify-center" role="radiogroup">
                        <label
                        v-for="(item, i) in tiers"
                        :key="i"
                        :for="`tier-${i}`"
                        class="p-4 md:py-[14px] md:px-[8px] border-2 rounded-lg cursor-pointer flex flex-col items-center justify-center text-center transition duration-200"
                        :class="[
                          selectedTier === item.name ? 'bg-[#D9DDFC] border-[#6073EE]' : 'border-[#e8e5de] hover:border-[#6073EE]'
                        ]"
                        >
                          <input 
                            type="radio" 
                            :id="`tier-${i}`"
                            name="selectedTier"
                            :value="item.name" 
                            v-model="selectedTier" 
                            class="sr-only"
                          />
                          <div class="text-center text-[0.72rem] md:text-[13px]">{{ item.name }}</div>
                        </label>
                      </div>
                    </fieldset>

                    <!-- Features -->
                    <fieldset class="mb-6 quiz__outcomes">
                      <legend :class="classes.heading">Reporting channels</legend>
                      <div :class="classes.subtitle">
                        Select the reporting channels that are relevant to your organisation.
                      </div>
                      <div class="mt-4 w-full grid grid-cols-2 sm:grid-cols-3 gap-2 justify-center">
                        <label
                        v-for="(selection, i) in featuresArray"
                        :key="i"
                        :for="`feature-${i}`"
                        class="py-1.5 px-1 md:py-[14px] md:px-[8px] border-2 hover:border-[#6073EE] rounded-lg cursor-pointer flex flex-col items-center justify-center text-center transition duration-200"
                        :class="[features.includes(selection.name) ? 'bg-[#D9DDFC] border-[#6073EE]' : 'border-[#e8e5de] hover:border-[#6073EE]' ]"
                        >
                          <input 
                            type="checkbox" 
                            :id="`feature-${i}`"
                            name="features"
                            :value="selection.name" 
                            v-model="features" 
                            class="sr-only"
                          />
                          <div class="text-center text-[0.72rem] md:text-[13px]">
                            <ImportSvg 
                            :size="selection.size" 
                            :name="selection.icon"
                            class="fill-gray-500"
                            :class="{ 'fill-blue-500': features.includes(selection.name) }"
                            />
                          </div>
                          <div class="text-center text-[0.72rem] md:text-[13px]">
                            {{ selection.name }}
                          </div>
                        </label>
                      </div>
                    </fieldset>

                    <!-- Additional Needs -->
                    <div class="mb-6">
                      <label for="needs" :class="classes.heading">Specific needs</label>
                      <div :class="classes.subtitle">
                        Tell us about the needs of your organisation. e.g. Will members of the public be reporting? Are you responding to a particular compliance requirement? Do you have any questions you would like answered?
                      </div>
                      <textarea
                      id="needs"
                      placeholder="Tell us about your needs"
                      v-model="needs"
                      class="min-h-[180px]"
                      :class="classes.input"
                      ></textarea>
                    </div>

                    <!-- Honeypot field - hidden from users, bots will fill it -->
                    <div aria-hidden="true" style="position: absolute; left: -9999px;">
                      <label for="company_url">Company URL</label>
                      <input type="text" v-model="companyUrl" id="company_url" name="company_url" tabindex="-1" autocomplete="off" />
                    </div>

                    <div class="mt-6">
                      <div class="flex">
                        <input 
                        @click="errorMessage = null"
                        id="consent" 
                        v-model="consent" 
                        type="checkbox" 
                        class="h-5 w-5 mt-[2px] cursor-pointer rounded border-[#E4E2DF] text-[#4d5ceb] focus:ring-transparent"
                        />
                        <label @click="errorMessage = null" for="consent" class="ml-2 block text-sm cursor-pointer">
                          I confirm I have read and agree to the processing of my above personal data as described in the <NuxtLink :to="$localePath('/policies/privacy')" class="underline" target="_blank">Privacy Policy</NuxtLink>.
                        </label>
                      </div> 
                    </div>

                    <!-- Submit Button -->
                    <div class="mt-8">
                      <button
                      :disabled="waiting"
                      @click="submitForm"
                      class="w-full inline-flex items-center justify-center rounded-md border border-transparent bg-[#096b72] px-5 py-3 text-md font-semibold text-white hover:bg-[#07484b]"
                      :class="{ 'opacity-60' : waiting }"
                      >
                        <template v-if="waiting">
                          <ElementLoadingSpinner />
                          <span>{{ sendingMessage || 'Sending...' }}</span>
                        </template>
                        <template v-else>Submit request</template>
                      </button>
                      <ElementSubmitStatus :message="waiting ? sendingMessage : ''" />
                    </div>
                    <div v-if="errorMessage" class="text-red-500 text-sm font-normal mt-4">
                      {{ errorMessage }}
                    </div>
                  </div>
                </section>

                <section class="quiz p-4 md:p-6 lg:p-8" v-else>
                  <div class="mt-2 max-w-[264px] sm:max-w-[400px] lg:max-w-[436px] ml-auto mr-auto align-left">
                    <h2 class="text-3xl font-bold text-gray-900 mb-6">
                      Success
                    </h2>
                    <p class="mt-2 text-base text-gray-500">
                      Thank you for your interest in Elker. We will be in touch shortly to discuss your requirements. If you have any questions in the meantime, please contact us at <a href="mailto:hello@elker.com">hello@elker.com</a>.
                    </p>
                  </div>
                </section>

              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <TransitionRoot as="template" :show="featureModalOpen">
      <Dialog as="div" class="relative z-10" @close="featureModalOpen = false">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel class="relative transform overflow-hidden rounded-xl bg-[#f9f8f6] text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div class="p-6">
                  <div class="flex items-start justify-between">
                    <DialogTitle as="h3" class="text-xl font-semibold text-gray-900">
                      {{ currentFeature.title }}
                    </DialogTitle>
                    <button 
                      @click="featureModalOpen = false"
                      class="ml-4 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 p-1"
                      aria-label="Close feature description dialog"
                    >
                      <Icon 
                        name="iconoir:xmark" 
                        class="h-6 w-6"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                  <div class="mt-4">
                    <p class="text-base text-gray-500">
                      {{ currentFeature.description }}
                    </p>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

  </main>
</template>

<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
const route = useRoute()
const userStore = useUserStore()
const contentSections = useContentSections()
const { getItems } = useCms()
const fields = useFields()
const pricingContent = usePricingContent()

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const sections = ref(pricingContent)
const isScrolled = ref(false)
const quoteOpen = ref(false)
const waiting = ref(false)
const errorMessage = ref(null)
const genericEmail = ref(false)
const email = ref('')
const name = ref('')
const organisation = ref('')
const size = ref('')
const needs = ref('')
const features = ref([])
const sent = ref(false)
const consent = ref(false)
const emailError = ref(null)
const selectedTier = ref(null)
const companyUrl = ref('') // Honeypot field
const formLoadedAt = ref(Date.now()) // Timestamp for spam detection

const classes = {
  subtitle: 'text-[13px] text-gray-500 font-normal mt-1 mb-3 max-w-[264px] sm:max-w-[400px] lg:max-w-[436px] ml-auto mr-auto align-left',
  heading: 'text-lg font-bold text-gray-900',
  input: 'input-text'
}

const blurEmailField = () => {
  genericEmail.value = false
  emailError.value = null
  if (isCommonEmail(email.value)) {
    genericEmail.value = true
  }
  if (!isValidEmail(email.value)) {
    emailError.value = 'Please enter a valid email address'
  }
}

const requestQuote = () => {
  selectedTier.value = 'Core'
  quoteOpen.value = true
}

const featureModalOpen = ref(false)
const currentFeature = ref({
  title: '',
  description: ''
})

const tooltip = (title, description) => {
  currentFeature.value = {
    title,
    description
  }
  featureModalOpen.value = true
}

const featuresArray = [
  {
    name: 'Whistleblowing',
    icon: 'whistle',
    size: 'md'
  },
  {
    name: 'Public interest disclosures',
    icon: 'building-columns',
    size: 'md'
  },
  {
    name: 'Psychosocial hazards and risks',
    icon: 'head-side-brain',
    size: 'md'
  },
  {
    name: 'Speak up',
    icon: 'face-exhaling',
    size: 'md'
  },
  {
    name: 'Incident reporting',
    icon: 'person-falling-burst',
    size: 'md'
  },
  {
    name: 'WHS reporting',
    icon: 'user-helmet-safety',
    size: 'md'
  },
  {
    name: 'Modern slavery reporting',
    icon: 'person-circle-exclamation',
    size: 'md'
  },
  {
    name: 'Feedback and suggestions',
    icon: 'face-smile-plus',
    size: 'md'
  }
]

const tiers = [
  {
    name: 'Core',
    price: 'Custom',
    background: 'bg-[#edf2ed]',
    headingColor: 'text-gray-900',
    textColor: 'text-gray-900',
    iconColor: 'text-gray-900',
    borderColor: 'border-[#d1d6d1]',
    buttonBackground: 'bg-[#5a6ded] border-2 border-[#3645d0]',
    buttonTextColor: 'text-white',
    href: '#',
    description: 'Essential reporting tools for small-to-medium-sized organisations.',
    features: [
      'Unlimited internal user seats',
      'Configurable reporting pathways',
      'Custom notifications',
      'Analytics dashboard',
      'Standard automations',
      'iOS & Android app'
    ],
    featured: false,
    cta: 'Request proposal',
    function: () => {
      requestQuote()
    }
  },
  {
    name: 'Enterprise',
    href: '#',
    price: 'Custom',
    background: 'bg-gray-900',
    iconColor: 'text-white',
    headingColor: 'text-white',
    textColor: 'text-white',
    borderColor: 'border-gray-900',
    buttonBackground: 'bg-[#5a6ded] border-2 border-[#3645d0]',
    buttonTextColor: 'text-white',
    description: 'Custom solution for large enterprises, global teams and complex needs.',
    features: [
      'Unlimited seats & pathways',
      'Custom fields & complex form logic',
      'Custom analytics & surveys',
      'External case management through trusted partners',
      'Custom data jurisdiction',
      'Dedicated account manager'
    ],
    featured: true,
    cta: `Contact us`,
    function: () => {
      selectedTier.value = 'Enterprise'
      quoteOpen.value = true
    }
  }
]

const toggleSection = (index) => {
  sections.value[index].isOpen = !sections.value[index].isOpen
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 0
}

const getSectionComponent = key => {
  return contentSections[key]
}

const isCommonEmail = email => {
  if (!email || email.length < 3) {
    return true
  }
  const commonDomains = [
    'bigpond.com',
    'bigpond.com.au',
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'outlook.com.au',
    'aol.com',
    'icloud.com',
    'msn.com',
    'live.com',
    'me.com',
    'mail.com',
    'comcast.net',
    'verizon.net',
    'cox.net',
    'sbcglobal.net',
    'ymail.com',
    'rocketmail.com',
    'att.net',
    'suddenlink.net',
    'protonmail.com',
    'zoho.com',
    'inbox.com',
    'yandex.com',
    'gmx.com',
    'fastmail.com',
    'tutanota.com'
  ]
  const [, domain] = email.split('@')
  if (domain === undefined) {
    return true
  }
  return commonDomains.includes(domain.toLowerCase())
}

const isValidEmail = email => {
  const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  return emailPattern.test(email)
}


// Funnel tracking. This is the quote form: the highest-intent lead form on the
// site and the one that fires elker_conversion, so it belongs in the funnel
// report alongside the others.
const formTracking = useFormTracking('quote')

const submitForm = async (e) => {
  e.preventDefault()
  waiting.value = true
  errorMessage.value = null
  formTracking.submitted()

  const userEmail = email.value
  const userName = name.value

  if (!userEmail.length || !userName.length) {
    errorMessage.value = `Please enter your full name and work email`
    formTracking.failed('missing-name-or-email')
    waiting.value = false
    return false
  }

  if (!size.value || size.value < 1) {
    errorMessage.value = `Please enter the size of your organisation (i.e. the approximate number of people who will report on the platform)`
    formTracking.failed('missing-size', 'size')
    waiting.value = false
    return false
  }

  if (!isValidEmail(userEmail)) {
    errorMessage.value = 'Your email address is not valid'
    formTracking.failed('invalid-email', 'email')
    waiting.value = false
    return false
  }

  if (isCommonEmail(userEmail) && !organisation.value) {
    errorMessage.value = `Please enter the name of your organisation. If you are a consultant, please provide your consultancy name.`
    formTracking.failed('missing-organisation', 'organisation')
    waiting.value = false
    return false
  }

  if (consent.value !== true) {
    errorMessage.value = 'To continue, please confirm we can use the above personal data in accordance with our Privacy Policy.'
    formTracking.failed('consent-not-given', 'consent')
    waiting.value = false
    return false
  }

  const responseString = `
    <p>
      ${name.value ? 'Name: ' + name.value + '<br>' : '' }
      ${organisation.value ? 'Organisation: ' + organisation.value + '<br>' : '' }
      ${email.value ? 'Email: ' + email.value + '<br>' : '' }
      ${size.value ? 'Size of organisation: ' + size.value + '<br>' : '' }
      ${selectedTier.value ? 'Selected tier: ' + selectedTier.value + '<br>' : '' }
      ${features.value ? 'Reporting channels: ' + features.value.join(', ') + '<br>' : '' }
      ${needs.value ? 'Needs: ' + needs.value + '<br>' : '' }
      ${userStore.utmSource ? 'Source: ' + userStore.utmSource + '<br>' : '' }
      ${userStore.utmMedium ? 'Medium: ' + userStore.utmMedium + '<br>' : '' }
      ${userStore.utmCampaign ? 'Campaign: ' + userStore.utmCampaign + '<br>' : '' }
      ${userStore.utmLandingPage ? 'Landing page: ' + userStore.utmLandingPage + '<br>' : '' }
    </p>
  `

  const quizData = {
    email: email.value,
    name: name.value,
    message: responseString,
    utmSource: userStore.utmSource,
    utmMedium: userStore.utmMedium,
    utmCampaign: userStore.utmCampaign,
    utmLandingPage: userStore.utmLandingPage,
    // Conversion attribution for server-side conversion firing.
    ...useConversionFields(),
    // Spam protection fields
    companyUrl: companyUrl.value,
    formLoadedAt: formLoadedAt.value
  }

  // The email-quality check (disposable domain / MX) runs inside /api/sendQuiz
  // itself, so the whole submit is one round trip.
  startSendingMessages()

  try {

    const sendQuiz = await $fetch('/api/sendQuiz', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(quizData),
    })

    if (!sendQuiz.ok) {
      errorMessage.value = sendQuiz.message || 'There has been an error. Please email us at hello@elker.com'
      formTracking.failed('server-rejected')
      return false
    }
      
    sent.value = true
    formTracking.ok({ recorded: sendQuiz.recorded !== false })

    // recorded: false marks a server-side decoy (spam gate tripped): show the
    // success panel as normal but do not fire conversion events for it.
    if (process.client && window.dataLayer && sendQuiz.recorded !== false) {
      // console.log('Enhanced conversion event: ' + email.value)
      window.dataLayer.push({
        event: 'elker_conversion',
        'enhanced_conversion_email': email.value
      })
      window.dataLayer.push({
        event: 'quiz_submit',
        action: 'submit',
        label: 'quiz_submit',
        noninteraction: false
      })
    }
  } catch(e) {
    errorMessage.value = 'There has been an error. Please email us at hello@elker.com'
    formTracking.failed('request-failed')
  } finally {
    stopSendingMessages()
    waiting.value = false
  }
}

const { sendingMessage, startSendingMessages, stopSendingMessages } = useSubmitProgress()


const { data } = await useAsyncData(
  currentLocale.value+'-page-pricing',
  () => getItems({
    collection: 'pricing',
    params: {
      fields
    },
  })
)

const page = data?.value


onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

const seo = computed(() => {
  if (page?.seo) {
    return useSeo(page.seo, route.fullPath)
  }
  return {}
})

useHead(seo)

</script>


<style lang="scss">
@import '@/assets/scss/_vars.scss';

.quiz {
  li {
    &:hover {
      svg path {
        fill: $elker-1;
      }
    }
  }
  svg {
    width: 30px;
    height: 30px;
    margin-bottom: 8px;
    display: inline-block;
    &.svg-md {
      width: 22px;
      height: 22px;
      margin-bottom: 10px;
      margin-top: 6px
    }
  }
  path {
    fill: $blue;
    transition: all 0.2s ease;
  }

}

input[type='checkbox']:checked, 
input[type='radio']:checked {
background-color: #5a6ded!important;
}
</style>